import { Box, Text as ChakraText, Flex, useToken } from "@chakra-ui/react";
import { GetProgrammaticCampaignPerformanceByDeviceRO } from "@intentsify/dto";
import { Option } from "@intentsify/types";
import { decimalToPercentage, toNumberDisplayValue } from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Card, MIN_MODAL_CONTENT_HEIGHT, Select } from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { useMemo, useState } from "react";
import { DisplayPerformanceFilters } from "../../../../types";
import { MdAnalytics } from "react-icons/md";
import { isPopulatedArray } from "@intentsify/utils";

type PerformanceByDeviceProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getPerformanceByDevice: (
    id: string,
    filters: DisplayPerformanceFilters,
  ) => Promise<GetProgrammaticCampaignPerformanceByDeviceRO>;
};

type Mode = "impressions" | "clicks" | "conversions";

const modeOptions: Option<Mode>[] = [
  {
    value: "impressions",
    label: "Impressions",
  },
  {
    value: "clicks",
    label: "Clicks",
  },
];

const MIN_IMPRESSIONS_THRESHOLD = 100;
const TITLE = "Performance by device";

export const PerformanceByDevice = (props: PerformanceByDeviceProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <PerformanceByDeviceContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const PerformanceByDeviceContent = ({
  campaignId,
  filters,
  getPerformanceByDevice,
}: PerformanceByDeviceProps) => {
  const [modeValue, setModeValue] = useState<Option<Mode>>(modeOptions[0]);

  const yellow = useToken("colors", "yellow.500");
  const green = useToken("colors", "green.500");
  const teal = useToken("colors", "teal.500");
  const blue = useToken("colors", "blue.500");

  const { data: rawData, isFetching } = useQuery({
    queryKey: ["getPerformanceByDevice", campaignId, filters],
    queryFn: async () => await getPerformanceByDevice(campaignId, filters),
  });

  const data = useMemo(() => {
    if (isFetching || !rawData?.performanceByDevice) {
      return [];
    }

    return rawData.performanceByDevice
      .filter(
        (deviceType) => deviceType.impressions >= MIN_IMPRESSIONS_THRESHOLD,
      )
      .map((deviceType) => ({
        label: deviceType.deviceName,
        value: deviceType[modeValue.value],
      }))
      .sort((a, b) => b.value - a.value);
  }, [isFetching, modeValue, rawData]);

  return (
    <Card
      isLoading={isFetching}
      title={TITLE}
      isExpandable
      hasData={!isFetching && isPopulatedArray(data)}
      noDataMessage="Currently there is no data available for the selected filters or visuals."
      noDataIcon={MdAnalytics}
    >
      {(isExpanded) => (
        <Flex
          flexDir="column"
          h={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : "100%"}
        >
          <Flex mb={2}>
            <Flex alignItems="center">
              <Box minWidth={300} padding={2}>
                <Select
                  isClearable={false}
                  isMulti={false}
                  value={modeValue}
                  options={modeOptions}
                  onChange={(val) => {
                    if (val) {
                      setModeValue(val);
                    }
                  }}
                />
              </Box>
            </Flex>
          </Flex>
          <Flex>
            <BarChart
              minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
              stretch
              valueAxisLabel={modeValue.label}
              orientation="horizontal"
              data={data}
              colorRange={[teal, yellow, green, teal, blue]}
              tooltipRenderer={
                modeValue.value === "clicks"
                  ? (textColor, tooltipData) => {
                      const d = rawData?.performanceByDevice.find(
                        (el) => el.deviceName === tooltipData.label,
                      );
                      return (
                        <>
                          <ChakraText fontSize="medium" color={textColor}>
                            <strong>{modeValue.label}:</strong>{" "}
                            {toNumberDisplayValue(tooltipData.value)}
                          </ChakraText>
                          {d && d.impressions !== 0 && (
                            <ChakraText fontSize="medium" color={textColor}>
                              <strong>CTR: </strong>{" "}
                              {decimalToPercentage(d.clicks / d.impressions)}%
                            </ChakraText>
                          )}
                        </>
                      );
                    }
                  : undefined
              }
              valueNumTicks={data.every((item) => item.value === 0) ? 1 : 6}
            />
          </Flex>
        </Flex>
      )}
    </Card>
  );
};
